<template>
    <div id="app" :class="companySkin">
        <v-app>
            <div class="main-content">
                <CompanyHeader />
                <AuthProvider>
                    <router-view />
                    <DevPanel v-if="showDevPanel" />
                    <DebugOverlay v-if="showOverlayDebug" />
                </AuthProvider>
            </div>

            <Modals />

            <CompanyFooter />
        </v-app>
    </div>
</template>

<script lang="ts">
    import { defineComponent, computed } from 'vue';
    import { useStore } from './store';
    import { configBase } from '@/config/base';
    import { isStenaCompany } from '@shared/config/market/market.helpers';
    import { feature } from '@libs/utils/feature/feature';
    import { Market } from '@shared/config/market/market.models';
    import Modals from './components/modals/Modals.vue';
    import DevPanel from '@/components/shared/DevPanel/DevPanel.vue';
    import CompanyHeader from '@/components/shared/CompanyHeader/CompanyHeader.vue';
    import CompanyFooter from '@/components/shared/CompanyFooter/CompanyFooter.vue';
    import AuthProvider from './providers/AuthProvider.vue';
    import DebugOverlay from '@/components/shared/DebugOverlay/DebugOverlay.vue';

    export default defineComponent({
        name: 'App',
        components: {
            DebugOverlay,
            DevPanel,
            Modals,
            CompanyHeader,
            CompanyFooter,
            AuthProvider,
        },
        head() {
            const store = useStore();

            const market = computed((): Market => store.getters['general/getMarket']);

            const lang = market.value?.locale ?? 'en';
            const title = market.value?.name ?? '';
            const favIcon = market.value?.assets.favIcon ?? '';

            return {
                title,
                link: [{ rel: 'icon', href: favIcon }],
                htmlAttrs: {
                    lang,
                },
            };
        },

        setup() {
            const store = useStore();

            const market = computed((): Market => store.getters['general/getMarket']);

            const showDevPanel = computed(() => configBase.devMode.enabled);

            const showOverlayDebug = feature('debug-overlay');

            const companySkin = computed(() =>
                isStenaCompany(market.value?.number) ? 'stena-app' : 'sembo-app'
            );

            return {
                market,
                companySkin,
                showDevPanel,
                showOverlayDebug,
            };
        },
    });
</script>

<style lang="scss">
    @forward './styles/layout.scss';
</style>
